import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ConfirmSheet from '@/components/ConfirmSheet';
import Avatar from '@/components/Avatar';
import { ShieldCheck, UserPlus, UserX, Phone } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminTeam() {
  const { api, user: me } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState('');
  const [promoting, setPromoting] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [demoteTarget, setDemoteTarget] = useState(null);

  const fetchAdmins = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/admins');
      setAdmins(data.admins || []);
    } catch {
      toast.error('Failed to load admin list');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const handlePromote = async (e) => {
    e.preventDefault();
    if (!phone.trim()) return;
    setPromoting(true);
    try {
      const { data } = await api.post('/admin/admins/promote', { phone: phone.trim() });
      toast.success(data.message || 'Admin access granted');
      setPhone('');
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to grant admin access');
    } finally {
      setPromoting(false);
    }
  };

  const requestDemote = (admin) => {
    if (!admin?.phone) {
      toast.error('This admin has no phone on record');
      return;
    }
    setDemoteTarget(admin);
  };

  const confirmDemote = async () => {
    if (!demoteTarget) return;
    setBusyId(demoteTarget.id);
    try {
      const { data } = await api.post('/admin/admins/demote', { phone: demoteTarget.phone });
      toast.success(data.message || 'Admin access revoked');
      setDemoteTarget(null);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revoke');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-team-section">
      {/* Grant access card */}
      <div className="bg-[#F6F8FB] rounded-2xl border border-[#E4E8EF] p-4">
        <div className="flex items-center gap-2 mb-3">
          <UserPlus className="w-4 h-4 text-[#0B4F3F]" />
          <h3 className="text-sm font-semibold text-[#0B1620] font-['Bricolage_Grotesque']">Grant Admin Access</h3>
        </div>
        <p className="text-xs text-[#5B6675] mb-3">
          Enter the phone number the user signs in with. They must have an existing account.
        </p>
        <form onSubmit={handlePromote} className="flex gap-2">
          <div className="flex-1 relative">
            <Phone className="w-4 h-4 text-[#5B6675] absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="admin-promote-phone-input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="e.g., +919876543210 or 9876543210"
              className="h-10 pl-9 bg-[#F6F8FB] border-[#E4E8EF] rounded-lg text-sm text-[#0B1620]"
              inputMode="tel"
            />
          </div>
          <Button
            data-testid="admin-promote-submit-btn"
            type="submit"
            disabled={promoting || !phone.trim()}
            className="h-10 px-4 bg-[#0B4F3F] hover:bg-[#0B4F3F]/90 text-[#F6F8FB] rounded-lg text-xs font-semibold"
          >
            {promoting ? 'Adding...' : 'Grant'}
          </Button>
        </form>
      </div>

      {/* Current admins */}
      <div className="bg-[#F6F8FB] rounded-2xl border border-[#E4E8EF] p-4">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-4 h-4 text-[#0B4F3F]" />
          <h3 className="text-sm font-semibold text-[#0B1620] font-['Bricolage_Grotesque']">
            Current Admins {admins.length > 0 && <span className="text-[#5B6675] font-normal">({admins.length})</span>}
          </h3>
        </div>

        {loading ? (
          Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="animate-pulse flex items-center gap-3 py-3 border-b border-[#E4E8EF] last:border-0">
              <div className="w-9 h-9 rounded-full bg-[#EFF2F7]" />
              <div className="flex-1 space-y-1">
                <div className="h-3 bg-[#EFF2F7] rounded w-1/2" />
                <div className="h-2.5 bg-[#EFF2F7] rounded w-1/3" />
              </div>
            </div>
          ))
        ) : admins.length === 0 ? (
          <p className="text-xs text-[#5B6675]/70 text-center py-6">No admins yet</p>
        ) : (
          <div className="divide-y divide-[#E4E8EF]">
            {admins.map((a) => {
              const isMe = me && (a.id === me._id || a.id === me.id);
              return (
                <div
                  key={a.id}
                  data-testid={`admin-row-${a.id}`}
                  className="flex items-center gap-3 py-3"
                >
                  <Avatar name={a.name} src={a.avatar} size={36} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#0B1620] truncate">
                      {a.name}
                      {isMe && <span className="ml-2 text-[10px] text-[#5B6675] font-normal">(you)</span>}
                    </p>
                    <p className="text-xs text-[#5B6675] truncate">{a.phone || a.email || '—'}</p>
                  </div>
                  {!isMe && (
                    <button
                      data-testid={`admin-demote-btn-${a.id}`}
                      onClick={() => requestDemote(a)}
                      disabled={busyId === a.id}
                      className="text-xs text-[#B91C1C] hover:bg-[#B91C1C]/5 px-2 py-1 rounded-lg flex items-center gap-1 disabled:opacity-50"
                    >
                      <UserX className="w-3.5 h-3.5" /> Revoke
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <ConfirmSheet
        open={!!demoteTarget}
        onOpenChange={(o) => !o && setDemoteTarget(null)}
        title="Revoke admin access?"
        description={demoteTarget ? `${demoteTarget.name} will lose access to the Frootex admin portal.` : ''}
        confirmLabel="Revoke access"
        cancelLabel="Keep as admin"
        destructive
        icon={<UserX className="w-6 h-6" strokeWidth={2.2} />}
        onConfirm={confirmDemote}
        loading={!!(demoteTarget && busyId === demoteTarget.id)}
        testId="revoke-admin"
      />
    </div>
  );
}
